/**
 * Select value of the "Unverändert lassen" option of a batch preconfiguration field. Applying the
 * preconfiguration skips such a field, so every target row keeps its current value for it.
 */
export const KEEP_PRECONFIGURATION_VALUE = "__keep__"

type KeepPreconfigurationValue = typeof KEEP_PRECONFIGURATION_VALUE

/** Preconfiguration field value: a concrete value for every target row, or "keep". */
export type PreconfigurationChoice<T> = T | KeepPreconfigurationValue

const KEEP_PRECONFIGURATION_LABEL = "Unverändert lassen"

const keepPreconfigurationOption = {
  value: KEEP_PRECONFIGURATION_VALUE,
  label: KEEP_PRECONFIGURATION_LABEL,
  name: KEEP_PRECONFIGURATION_LABEL,
} as const

export const isKeepPreconfigurationChoice = (value: unknown): value is KeepPreconfigurationValue =>
  value === KEEP_PRECONFIGURATION_VALUE

/** Options of a preconfiguration select: "keep" first, then the concrete values. */
export const withKeepPreconfigurationOption = <T extends object>(
  options: readonly T[],
): Array<T | typeof keepPreconfigurationOption> => [keepPreconfigurationOption, ...options]

/**
 * Reconciles a changed multi-select that offers "keep" beside concrete values. Choosing "keep"
 * clears the concrete values; choosing a concrete value while "keep" is active clears "keep".
 */
export const reconcileKeepPreconfigurationSelection = (
  previous: readonly string[],
  incoming: readonly string[],
): string[] => {
  const choseKeep =
    incoming.includes(KEEP_PRECONFIGURATION_VALUE) &&
    (!previous.includes(KEEP_PRECONFIGURATION_VALUE) || incoming.length === 1)

  if (choseKeep) return [KEEP_PRECONFIGURATION_VALUE]

  return incoming.filter((value) => value !== KEEP_PRECONFIGURATION_VALUE)
}

/** Single-select value from a select change event, or null when the event carries none. */
export const preconfigurationSelectEventValue = (event: Event | undefined): string | null => {
  const detail = (event as CustomEvent<{ value?: unknown }> | undefined)?.detail

  if (!detail || typeof detail !== "object" || !("value" in detail)) return null

  return detail.value === null || detail.value === undefined ? null : String(detail.value)
}
